import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const TIER_CONFIG = {
  whale:     { label: '큰손',   color: '#FFD700', bg: 'rgba(255,215,0,0.12)' },
  potential: { label: '잠재큰손', color: '#C0C0C0', bg: 'rgba(192,192,192,0.10)' },
  regular:   { label: '단골',   color: '#CD7F32', bg: 'rgba(205,127,50,0.10)' },
  normal:    { label: '일반',   color: '#8B8B9E', bg: 'rgba(139,139,158,0.08)' },
};

/**
 * /chat 페이지 — 큰손/단골 실시간 채팅 피드
 *
 * 기존 SSE 스트림(/notifications/stream)에서 chat_message 이벤트만 필터링.
 * 큰손/잠재큰손/단골 등급 시청자의 YouTube 라이브 채팅 메시지만 표시.
 */
function ChatPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('all');
  const chatEndRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let reconnectTimer = null;
    let unmounted = false;
    let esRef = null;

    const connectSSE = () => {
      if (unmounted) return;

      const es = new EventSource(`${API_BASE}/notifications/stream?token=${token}`);
      esRef = es;

      es.onopen = () => setConnected(true);

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            setConnected(true);
            return;
          }

          if (data.type === 'chat_message') {
            setMessages(prev => [...prev, {
              id: `${data.channelId}_${data.publishedAt}_${Date.now()}`,
              displayName: data.displayName,
              profileImage: data.profileImage,
              text: data.text,
              tier: data.tier,
              dbNickname: data.dbNickname,
              totalDonated: data.totalDonated,
              todayDonated: data.todayDonated,
              isModerator: data.isModerator,
              isMember: data.isMember,
              publishedAt: data.publishedAt,
              timestamp: Date.now(),
            }].slice(-200));
          }
        } catch (err) {
          console.error('[Chat SSE] Parse error:', err);
        }
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (!unmounted) {
          reconnectTimer = setTimeout(connectSSE, 5000);
        }
      };
    };

    connectSSE();

    return () => {
      unmounted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (esRef) esRef.close();
    };
  }, []);

  useEffect(() => {
    if (autoScroll && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAutoScroll(isAtBottom);
  };

  const filteredMessages = filter === 'all'
    ? messages
    : messages.filter(m => m.tier === filter);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatAmount = (num) => {
    if (!num) return '';
    if (num >= 10000) return `${(num / 10000).toFixed(1)}만원`;
    return `${num.toLocaleString()}원`;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      {/* 헤더 */}
      <div className="nav">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="nav-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
            큰손탐지기
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>/ 실시간 채팅</span>
        </div>
        <div className="nav-user">
          <span
            style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: connected ? '#4ade80' : '#ef4444', marginRight: 4,
            }}
          />
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {connected ? 'SSE 연결됨' : '연결 중...'}
          </span>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}
            style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
            대시보드
          </button>
          <button className="btn btn-outline" onClick={onLogout}
            style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
            로그아웃
          </button>
        </div>
      </div>

      {/* 필터 + 통계 */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12, flexWrap: 'wrap', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { key: 'all', label: '전체' },
              { key: 'whale', label: '큰손' },
              { key: 'potential', label: '잠재큰손' },
              { key: 'regular', label: '단골' },
              { key: 'normal', label: '일반' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: '4px 12px', fontSize: '0.8rem', borderRadius: 16,
                  border: filter === f.key ? 'none' : '1px solid var(--border)',
                  background: filter === f.key
                    ? (f.key === 'all' ? 'var(--primary)' : TIER_CONFIG[f.key]?.bg || 'var(--primary)')
                    : 'transparent',
                  color: filter === f.key
                    ? (f.key === 'all' ? '#fff' : TIER_CONFIG[f.key]?.color || '#fff')
                    : 'var(--text-muted)',
                  cursor: 'pointer', fontWeight: filter === f.key ? 600 : 400,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            메시지 {filteredMessages.length}개
            {!autoScroll && (
              <button
                onClick={() => {
                  setAutoScroll(true);
                  chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  marginLeft: 8, padding: '2px 8px', fontSize: '0.75rem',
                  background: 'var(--primary)', color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer',
                }}
              >
                ↓ 최신으로
              </button>
            )}
          </div>
        </div>

        {/* 채팅 영역 */}
        <div
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 12, height: 'calc(100vh - 160px)',
            overflowY: 'auto', padding: 16,
          }}
        >
          {filteredMessages.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: 'var(--text-muted)',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>💬</div>
              <div style={{ fontSize: '0.9rem', marginBottom: 4 }}>
                실시간 채팅 대기 중...
              </div>
              <div style={{ fontSize: '0.8rem' }}>
                YouTube 모니터링이 활성화되면 시청자들의 채팅 메시지가 여기에 표시됩니다.
              </div>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const tier = TIER_CONFIG[msg.tier];
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex', gap: 10, padding: '8px 4px',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                    animation: 'fadeIn 0.3s ease-in',
                  }}
                >
                  {/* 프로필 이미지 */}
                  <div style={{ flexShrink: 0 }}>
                    {msg.profileImage ? (
                      <img
                        src={msg.profileImage}
                        alt=""
                        style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${tier?.color || '#555'}` }}
                      />
                    ) : (
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: tier?.bg || '#333', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.8rem', border: `2px solid ${tier?.color || '#555'}`,
                      }}>
                        {msg.displayName?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>

                  {/* 메시지 본문 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.65rem', padding: '1px 6px', borderRadius: 8,
                        background: tier?.bg || '#333', color: tier?.color || '#aaa',
                        fontWeight: 600,
                      }}>
                        {tier?.label || msg.tier}
                      </span>

                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: tier?.color || 'var(--text)' }}>
                        {msg.displayName}
                      </span>

                      {msg.dbNickname && msg.dbNickname !== msg.displayName && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          ({msg.dbNickname})
                        </span>
                      )}

                      {msg.isModerator && (
                        <span style={{ fontSize: '0.65rem', color: '#5B7FFF', background: 'rgba(91,127,255,0.1)', padding: '1px 5px', borderRadius: 6 }}>
                          관리자
                        </span>
                      )}
                      {msg.isMember && (
                        <span style={{ fontSize: '0.65rem', color: '#2BA640', background: 'rgba(43,166,64,0.1)', padding: '1px 5px', borderRadius: 6 }}>
                          멤버
                        </span>
                      )}

                      {(msg.todayDonated > 0 || msg.totalDonated > 0) && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'flex', gap: 4 }}>
                          {msg.todayDonated > 0 && (
                            <span style={{ color: '#FF6B6B' }}>오늘 {formatAmount(msg.todayDonated)}</span>
                          )}
                          {msg.totalDonated > 0 && (
                            <span>누적 {formatAmount(msg.totalDonated)}</span>
                          )}
                        </span>
                      )}

                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                        {formatTime(msg.publishedAt)}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.88rem', lineHeight: 1.5, wordBreak: 'break-word' }}>
                      {msg.text}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ChatPage;
